/**
 * DislikesStep.js — Assessment Wizard, Screen 9 "Dislikes"
 *
 * Captures up to MAX_DISLIKES foods to hard-exclude from the candidate
 * pool before the AI Recommendation Layer ever sees it — the same
 * mechanism as allergen exclusion on Screen 6, just lower severity. Built
 * on the shared AssessmentStepShell so progress bar, step counter,
 * question header, and Previous/Next footer stay identical to every other
 * screen.
 *
 * Entirely optional per the FSD — Next is never disabled here, matching
 * the "Optional; max 15 selections" validation rule. Structurally this
 * mirrors LikesStep.js closely (same searchable-chip pattern); they're
 * kept as separate components/files rather than a single parameterized
 * one because Screen 8 and Screen 9 have independent validation, event
 * names, and downstream semantics (ranking boost vs. hard exclusion).
 *
 * Search suggestions are shaped by the person's own Screen 7 answers
 * (Diet Type / Cuisine / Cooking Time / Budget), passed down as
 * `foodPreferences` by DietPlannerWizard.js, same as LikesStep.js: Diet
 * Type hard-excludes incompatible catalog items from suggestions (no
 * point suggesting "Chicken" as a dislike to someone who's already
 * Vegetarian — it can never appear in their plan either way), and
 * Cooking Time / Budget / Cuisine soft-rank closer matches first without
 * excluding anything, per FSD v2.1 §6. See DislikesStep.foodFilters.js.
 *
 * Also hard-excludes anything already picked on Screen 8 (Likes), passed
 * down as `likes`: a food marked as liked can't be suggested here as a
 * dislike (contradictory — and the AI Recommendation Layer would have
 * two conflicting signals for the same ingredient otherwise). This is a
 * suggestions-only exclusion, same as everything else in this file — a
 * person can still type either one as a free-text custom entry on either
 * screen if they really want to, same as before.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useDislikesAnalytics from "./useDislikesAnalytics";
import { validateDislikes } from "./DislikesStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  FOOD_SEARCH_ITEMS,
  MAX_DISLIKES,
  ALLOW_CUSTOM_ENTRIES,
  DISLIKES_INITIAL_DATA,
} from "./DislikesStep.constants";
import { filterAndRankFoodItems, describeActiveFilters } from "./DislikesStep_foodFilters";
import {
  FieldGroup,
  FieldLabel,
  HelperValue,
  SearchInput,
  FilterNote,
  SuggestionGrid,
  SuggestionChip,
  SelectedGrid,
  SelectedChip,
  RemoveChipButton,
  EmptyState,
} from "./DislikesStep.styles";

const STEP_NUMBER = STEP_NUMBERS.DISLIKES;

// Turns "Sweet Potato" into a stable-ish slug id for custom entries typed
// by the user (foods not in FOOD_SEARCH_ITEMS). Doesn't attempt to
// dedupe against the catalog's real ids beyond a simple slug match — good
// enough for an exclusion list, not a foreign key.
const slugify = (label) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {object}   [props.foodPreferences] - Screen 7 "Food Preferences"
 *   answers, forwarded by the wizard: { dietType, cuisines, cookingTime,
 *   budget }. Used only to shape search suggestions here — Screen 7 itself
 *   remains the source of truth for hard-filtering the actual plan.
 * @param {object}   [props.likes]       - Screen 8 "Likes" answer,
 *   forwarded by the wizard: { likes: string[] }. Any id in here is
 *   excluded from the Dislikes catalog pool — see catalogPool below.
 * @param {function} props.onPrevious    - Go to Screen 8 (Likes).
 * @param {function} props.onNext        - Go to Screen 10, receives
 *   { dislikes: string[] }.
 */
const DislikesStep = ({
  sessionId,
  initialData = null,
  foodPreferences = null,
  likes = null,
  onPrevious,
  onNext,
}) => {
  const analytics = useDislikesAnalytics();
  const [formData, setFormData] = useState({ ...DISLIKES_INITIAL_DATA, ...initialData });
  const [query, setQuery] = useState("");

  const { isValid, errors } = useMemo(() => validateDislikes(formData), [formData]);
  const atCap = formData.dislikes.length >= MAX_DISLIKES;

  // Ids picked as a "like" on Screen 8 — kept out of the Dislikes catalog
  // pool below regardless of query, so they never surface as a
  // suggestion or an exact-label Enter match here.
  const likedFoodIds = useMemo(
    () => (Array.isArray(likes?.likes) ? likes.likes : []),
    [likes]
  );

  // Diet Type hard-filtered, Cooking Time/Budget/Cuisine soft-ranked, then
  // anything already liked on Screen 8 is removed — recomputed only when
  // the Screen 7 answers or the Screen 8 selections actually change.
  const catalogPool = useMemo(() => {
    const filtered = filterAndRankFoodItems(FOOD_SEARCH_ITEMS, foodPreferences);
    if (likedFoodIds.length === 0) return filtered;
    return filtered.filter((item) => !likedFoodIds.includes(item.id));
  }, [foodPreferences, likedFoodIds]);

  const filterNote = useMemo(() => describeActiveFilters(foodPreferences), [foodPreferences]);

  const selectedLabelById = useMemo(() => {
    const map = new Map(FOOD_SEARCH_ITEMS.map((item) => [item.id, item.label]));
    // Custom entries store their own label as the id's title-cased form —
    // fall back to that when the id isn't in the catalog.
    return (id) => map.get(id) ?? id.replace(/_/g, " ");
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return catalogPool
      .filter((item) => item.label.toLowerCase().includes(q) && !formData.dislikes.includes(item.id))
      .slice(0, 8);
  }, [query, formData.dislikes, catalogPool]);

  const addDislike = useCallback(
    (foodId) => {
      if (atCap || formData.dislikes.includes(foodId)) return;
      setFormData((prev) => ({ ...prev, dislikes: [...prev.dislikes, foodId] }));
      analytics.trackDislikeAdd(sessionId, foodId);
      setQuery("");
    },
    [atCap, formData.dislikes, analytics, sessionId]
  );

  const removeDislike = useCallback(
    (foodId) => {
      setFormData((prev) => ({ ...prev, dislikes: prev.dislikes.filter((id) => id !== foodId) }));
      analytics.trackDislikeRemove(sessionId, foodId);
    },
    [analytics, sessionId]
  );

  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const handleQueryKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      const trimmed = query.trim();
      if (!trimmed || atCap) return;

      // Exact catalog match takes priority over minting a custom id.
      // Matched against catalogPool (not the raw catalog) so a food hidden
      // by the person's own diet-type answer can't be added via an
      // exact-label Enter either — free typing still falls through to a
      // custom entry below, which is intentionally not filtered (see
      // DislikesStep.constants.js).
      const catalogMatch = catalogPool.find(
        (item) => item.label.toLowerCase() === trimmed.toLowerCase()
      );
      if (catalogMatch) {
        addDislike(catalogMatch.id);
        return;
      }
      if (ALLOW_CUSTOM_ENTRIES) {
        addDislike(slugify(trimmed));
      }
    },
    [query, atCap, addDislike, catalogPool]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Dislikes (Screen 9) returns to Likes (Screen 8).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Dislikes is optional, this only fires if
      // something manages to push more than MAX_DISLIKES entries in
      // programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    analytics.trackStepNext(sessionId, formData);
    // Advances the wizard to Screen 10 (Meal Schedule).
    onNext?.(formData);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Anything you'd rather avoid?"
      description="We'll exclude these from your plan entirely — optional, pick up to 15."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel htmlFor="dislikesSearch">
          Search foods{" "}
          <HelperValue>
            ({formData.dislikes.length}/{MAX_DISLIKES} selected)
          </HelperValue>
        </FieldLabel>
        <SearchInput
          id="dislikesSearch"
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleQueryKeyDown}
          placeholder={atCap ? "Limit reached — remove one to add another" : "e.g. mushroom, bread, coffee…"}
          disabled={atCap}
          autoComplete="off"
        />
        {filterNote && <FilterNote>{filterNote}</FilterNote>}
        {suggestions.length > 0 && (
          <SuggestionGrid>
            {suggestions.map((item) => (
              <SuggestionChip key={item.id} type="button" onClick={() => addDislike(item.id)} disabled={atCap}>
                + {item.label}
              </SuggestionChip>
            ))}
          </SuggestionGrid>
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel id="dislikesSelectedLabel">Selected</FieldLabel>
        <SelectedGrid role="group" aria-labelledby="dislikesSelectedLabel">
          {formData.dislikes.length === 0 ? (
            <EmptyState>Nothing selected yet.</EmptyState>
          ) : (
            formData.dislikes.map((id) => (
              <SelectedChip key={id}>
                {selectedLabelById(id)}
                <RemoveChipButton
                  type="button"
                  aria-label={`Remove ${selectedLabelById(id)}`}
                  onClick={() => removeDislike(id)}
                >
                  ×
                </RemoveChipButton>
              </SelectedChip>
            ))
          )}
        </SelectedGrid>
      </FieldGroup>
    </AssessmentStepShell>
  );
};

DislikesStep.displayName = "DislikesStep";
export default React.memo(DislikesStep);